import React, { useRef, useState } from 'react';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Code,
  Highlighter,
  Image,
  IndentDecrease,
  IndentIncrease,
  Italic,
  Link,
  List,
  ListOrdered,
  MoreVertical,
  Redo,
  Strikethrough,
  Tag,
  Type,
  Underline,
  Undo,
  Zap,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { cn } from '../../lib/utils';
import ColorPickerPopover from './ColorPickerPopover';
import {
  BLOCK_TYPES,
  DEFAULT_FONT_FAMILY,
  DEFAULT_FONT_SIZE,
  DEFAULT_LINE_HEIGHT,
  DEFAULT_TEXT_COLOR,
  FONT_FAMILIES,
  FONT_SIZES,
  LINE_HEIGHTS,
} from '../../utils/richEmailEditorUtils';

const DEFAULT_HIGHLIGHT_COLOR = '#ffff00';

const ToolbarTip = ({ label, children, disabled }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className={cn(disabled && 'pointer-events-none opacity-50')}>{children}</span>
    </TooltipTrigger>
    <TooltipContent side="top" className="bg-slate-900 text-white border-0">
      {label}
    </TooltipContent>
  </Tooltip>
);

const ToolbarButton = ({ active, disabled, onClick, children, testId, buttonRef }) => (
  <button
    ref={buttonRef}
    type="button"
    data-testid={testId}
    disabled={disabled}
    onMouseDown={(e) => {
      e.preventDefault();
      onClick?.(e);
    }}
    className={cn(
      'h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-700 hover:bg-blue-50 transition-colors',
      active && 'bg-blue-100 text-blue-700',
      disabled && 'opacity-50 cursor-not-allowed'
    )}
  >
    {children}
  </button>
);

const ToolbarSelect = ({ value, onValueChange, options, widthClass, testId, renderItem }) => (
  <Select value={value} onValueChange={onValueChange}>
    <SelectTrigger
      data-testid={testId}
      className={cn(
        'h-8 border-slate-200 bg-white text-xs focus:ring-2 focus:ring-blue-500/20',
        widthClass
      )}
    >
      <SelectValue />
    </SelectTrigger>
    <SelectContent className="bg-white border border-slate-200 shadow-lg max-h-64">
      {options.map((option) => {
        const val = typeof option === 'string' ? option : option.value;
        const label = typeof option === 'string' ? option : option.label;
        return (
          <SelectItem key={val} value={val} className="text-sm">
            {renderItem ? renderItem(option) : label}
          </SelectItem>
        );
      })}
    </SelectContent>
  </Select>
);

const RichEmailToolbar = ({
  disabled = false,
  fontFamily = DEFAULT_FONT_FAMILY,
  fontSize = DEFAULT_FONT_SIZE,
  blockType = 'p',
  lineHeight = DEFAULT_LINE_HEIGHT,
  textColor = DEFAULT_TEXT_COLOR,
  backgroundColor = DEFAULT_HIGHLIGHT_COLOR,
  onFontFamilyChange,
  onFontSizeChange,
  onBlockTypeChange,
  onLineHeightChange,
  onTextColorChange,
  onBackgroundColorChange,
  onBeforeColorPickerOpen,
  onMergeFields,
  onWriteWithAI,
  onUndo,
  onRedo,
  onBold,
  onItalic,
  onUnderline,
  onStrikethrough,
  onBulletList,
  onNumberedList,
  onAlignLeft,
  onAlignCenter,
  onAlignRight,
  onIndent,
  onOutdent,
  onLink,
  onImage,
  onSourceCode,
  canRedo = false,
  mergeFieldsButtonRef,
}) => {
  const [textColorOpen, setTextColorOpen] = useState(false);
  const [bgColorOpen, setBgColorOpen] = useState(false);
  const textColorButtonRef = useRef(null);
  const bgColorButtonRef = useRef(null);

  const openTextColorPicker = () => {
    if (disabled) return;
    onBeforeColorPickerOpen?.();
    setBgColorOpen(false);
    setTextColorOpen(true);
  };

  const openBgColorPicker = () => {
    if (disabled) return;
    onBeforeColorPickerOpen?.();
    setTextColorOpen(false);
    setBgColorOpen(true);
  };

  return (
    <TooltipProvider delayDuration={250}>
      <div
        className="rounded-t-lg border border-slate-200 bg-white p-2 space-y-1"
        data-testid="rich-email-toolbar"
      >
        <div className="flex flex-wrap items-center gap-1">
          <ToolbarTip label="Merge Fields" disabled={disabled}>
            <ToolbarButton
              disabled={disabled}
              onClick={onMergeFields}
              testId="toolbar-merge-fields"
              buttonRef={mergeFieldsButtonRef}
            >
              <Tag size={15} />
            </ToolbarButton>
          </ToolbarTip>
          <ToolbarTip label="Write with AI" disabled={disabled}>
            <ToolbarButton disabled={disabled} onClick={onWriteWithAI} testId="toolbar-write-ai">
              <Zap size={15} />
            </ToolbarButton>
          </ToolbarTip>
          <div className="w-px h-5 bg-slate-200 mx-0.5" />
          <ToolbarTip label="Undo" disabled={disabled}>
            <ToolbarButton disabled={disabled} onClick={onUndo} testId="toolbar-undo">
              <Undo size={15} />
            </ToolbarButton>
          </ToolbarTip>
          <ToolbarTip label="Redo" disabled={disabled || !canRedo}>
            <ToolbarButton disabled={disabled || !canRedo} onClick={onRedo} testId="toolbar-redo">
              <Redo size={15} />
            </ToolbarButton>
          </ToolbarTip>
          <div className="w-px h-5 bg-slate-200 mx-0.5" />
          <ToolbarTip label="Bold" disabled={disabled}>
            <ToolbarButton disabled={disabled} onClick={onBold} testId="toolbar-bold">
              <Bold size={15} />
            </ToolbarButton>
          </ToolbarTip>
          <ToolbarTip label="Italic" disabled={disabled}>
            <ToolbarButton disabled={disabled} onClick={onItalic} testId="toolbar-italic">
              <Italic size={15} />
            </ToolbarButton>
          </ToolbarTip>
          <ToolbarTip label="Underline" disabled={disabled}>
            <ToolbarButton disabled={disabled} onClick={onUnderline} testId="toolbar-underline">
              <Underline size={15} />
            </ToolbarButton>
          </ToolbarTip>
          <ToolbarTip label="Strikethrough" disabled={disabled}>
            <ToolbarButton disabled={disabled} onClick={onStrikethrough} testId="toolbar-strikethrough">
              <Strikethrough size={15} />
            </ToolbarButton>
          </ToolbarTip>
          <ToolbarSelect
            testId="toolbar-font-family"
            value={fontFamily}
            onValueChange={onFontFamilyChange}
            widthClass="w-[110px]"
            options={FONT_FAMILIES}
            renderItem={(family) => (
              <span style={{ fontFamily: family === 'Sans-serif' ? 'sans-serif' : family }}>{family}</span>
            )}
          />
        </div>

        <div className="flex flex-wrap items-center gap-1">
          <ToolbarSelect
            testId="toolbar-font-size"
            value={fontSize}
            onValueChange={onFontSizeChange}
            widthClass="w-[72px]"
            options={FONT_SIZES}
          />
          <ToolbarSelect
            testId="toolbar-block-type"
            value={blockType}
            onValueChange={onBlockTypeChange}
            widthClass="w-[92px]"
            options={BLOCK_TYPES.map((b) => ({ value: b.tag, label: b.label }))}
          />
          <ToolbarSelect
            testId="toolbar-line-height"
            value={lineHeight}
            onValueChange={onLineHeightChange}
            widthClass="w-[64px]"
            options={LINE_HEIGHTS}
          />
          <ToolbarTip label="Text color" disabled={disabled}>
            <ToolbarButton
              disabled={disabled}
              testId="toolbar-text-color"
              buttonRef={textColorButtonRef}
              active={textColorOpen}
              onClick={openTextColorPicker}
            >
              <span className="flex flex-col items-center leading-none">
                <Type size={15} />
                <span
                  className="mt-0.5 h-0.5 w-4 rounded"
                  style={{ backgroundColor: textColor }}
                />
              </span>
            </ToolbarButton>
          </ToolbarTip>
          <ColorPickerPopover
            mode="text"
            value={textColor}
            open={textColorOpen}
            onClose={() => setTextColorOpen(false)}
            anchorRef={textColorButtonRef}
            onChange={({ hex }) => {
              onTextColorChange?.(hex);
            }}
          />
          <ToolbarTip label="Background color" disabled={disabled}>
            <ToolbarButton
              disabled={disabled}
              testId="toolbar-bg-color"
              buttonRef={bgColorButtonRef}
              active={bgColorOpen}
              onClick={openBgColorPicker}
            >
              <span className="flex flex-col items-center leading-none">
                <Highlighter size={15} />
                <span
                  className="mt-0.5 h-0.5 w-4 rounded"
                  style={{ backgroundColor: backgroundColor }}
                />
              </span>
            </ToolbarButton>
          </ToolbarTip>
          <ColorPickerPopover
            mode="background"
            value={backgroundColor}
            open={bgColorOpen}
            onClose={() => setBgColorOpen(false)}
            anchorRef={bgColorButtonRef}
            onChange={({ hex }) => {
              onBackgroundColorChange?.(hex);
            }}
          />
          <ToolbarTip label="Disc List" disabled={disabled}>
            <ToolbarButton disabled={disabled} onClick={onBulletList} testId="toolbar-bullet-list">
              <List size={15} />
            </ToolbarButton>
          </ToolbarTip>
          <ToolbarTip label="Numbered List" disabled={disabled}>
            <ToolbarButton disabled={disabled} onClick={onNumberedList} testId="toolbar-numbered-list">
              <ListOrdered size={15} />
            </ToolbarButton>
          </ToolbarTip>
          <ToolbarTip label="Align" disabled={disabled}>
            <ToolbarButton disabled={disabled} onClick={onAlignLeft} testId="toolbar-align-left">
              <AlignLeft size={15} />
            </ToolbarButton>
          </ToolbarTip>
        </div>

        <div className="flex flex-wrap items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                data-testid="toolbar-more-menu"
                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-700 hover:bg-blue-50"
              >
                <MoreVertical size={15} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-white">
              <DropdownMenuItem onClick={onAlignCenter}>Align center</DropdownMenuItem>
              <DropdownMenuItem onClick={onAlignRight}>Align right</DropdownMenuItem>
              <DropdownMenuItem onClick={onIndent}>Increase indent</DropdownMenuItem>
              <DropdownMenuItem onClick={onOutdent}>Decrease indent</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <ToolbarTip label="Add Link" disabled={disabled}>
            <ToolbarButton disabled={disabled} onClick={onLink} testId="toolbar-link">
              <Link size={15} />
            </ToolbarButton>
          </ToolbarTip>
          <ToolbarTip label="Add Image" disabled={disabled}>
            <ToolbarButton disabled={disabled} onClick={onImage} testId="toolbar-image">
              <Image size={15} />
            </ToolbarButton>
          </ToolbarTip>
          <ToolbarTip label="Source code" disabled={disabled}>
            <ToolbarButton disabled={disabled} onClick={onSourceCode} testId="toolbar-source-code">
              <Code size={15} />
            </ToolbarButton>
          </ToolbarTip>
          <ToolbarTip label="Align" disabled={disabled}>
            <ToolbarButton disabled={disabled} onClick={onOutdent} testId="toolbar-outdent">
              <IndentDecrease size={15} />
            </ToolbarButton>
          </ToolbarTip>
          <ToolbarTip label="Align" disabled={disabled}>
            <ToolbarButton disabled={disabled} onClick={onIndent} testId="toolbar-indent">
              <IndentIncrease size={15} />
            </ToolbarButton>
          </ToolbarTip>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default RichEmailToolbar;
